import type { QuoteBudgetLocalDraft } from "../types/localDraft";

/** 区分缓存能力不足与不可覆盖的业务冲突。 */
export class QuoteBudgetLocalDraftError extends Error {
  constructor(public readonly kind: "storage" | "conflict" | "invalid", message: string) {
    super(message);
    this.name = "QuoteBudgetLocalDraftError";
  }
}

/** 缓存只使用业务用户与预算身份，不读取认证票据。 */
export function quoteBudgetLocalDraftKey(userId: string, budgetOrderId: string): string {
  return `flower-master:quote-budget-draft:v1:${encodeURIComponent(userId)}:${encodeURIComponent(budgetOrderId)}`;
}

/** 包装浏览器存储异常，不能因容量不足冻结业务编辑。 */
function storage<T>(action: () => T): T {
  try { return action(); }
  catch (error) {
    if (error instanceof QuoteBudgetLocalDraftError) throw error;
    throw new QuoteBudgetLocalDraftError("storage", "本机存储不可用或空间不足，尚未缓存；仍可编辑并保存到数据库，或导出草稿。");
  }
}

/** 判断 JSON 对象，拒绝数组及空值冒充工作态。 */
function record(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/** 只接受可供编辑器消费的完整骨架，损坏原文留在原键供导出。 */
export function parseQuoteBudgetLocalDraft(raw: string, userId: string, budgetOrderId: string): QuoteBudgetLocalDraft {
  const invalid = () => new QuoteBudgetLocalDraftError("invalid", "本机草稿格式不兼容，已保留原始备份，请先导出核对。");
  let value: unknown;
  try { value = JSON.parse(raw); } catch { throw invalid(); }
  if (!record(value) || value.schemaVersion !== 1 || value.userId !== userId || value.budgetOrderId !== budgetOrderId
    || typeof value.baseRevision !== "string" || !value.baseRevision || typeof value.changeId !== "string" || !value.changeId
    || (value.basedOnChangeId !== undefined && (typeof value.basedOnChangeId !== "string" || !value.basedOnChangeId))
    || typeof value.updatedAt !== "string" || !Number.isFinite(Date.parse(value.updatedAt)) || !record(value.payload)) throw invalid();
  const { draft, lines, workspace } = value.payload;
  if (!record(draft) || typeof draft.teacherPays !== "boolean"
    || [draft.studentCount, draft.teacherCount, draft.guideCount, draft.driverCount]
      .some((count) => typeof count !== "number" || !Number.isInteger(count) || count < 0 || count > 10000)
    || !Array.isArray(lines) || !record(workspace) || !record(workspace.drawers)) throw invalid();
  const categories = ["stay", "point", "meal", "air", "ship", "bus", "crew", "misc"];
  if (typeof workspace.activeCategory !== "string" || ![...categories, "rail"].includes(workspace.activeCategory)
    || (workspace.activeDrawerKey !== null && typeof workspace.activeDrawerKey !== "string")
    || (workspace.formula !== null && (!record(workspace.formula) || typeof workspace.formula.lineId !== "string" || typeof workspace.formula.text !== "string"
      || (workspace.formula.railRoles !== undefined && (!record(workspace.formula.railRoles)
        || Object.values(workspace.formula.railRoles).some((item) => typeof item !== "string")))))
    || (workspace.peopleInputs !== undefined && (!record(workspace.peopleInputs) || Object.values(workspace.peopleInputs).some((item) => typeof item !== "string")))) throw invalid();
  const railFee = workspace.formula?.railFee;
  if (railFee !== undefined && (!record(railFee) || typeof railFee.name !== "string"
    || !["学生", "老师", "导游", "司机"].includes(String(railFee.role)) || typeof railFee.isNew !== "boolean")) throw invalid();
  if (Object.values(workspace.drawers).some((drawer) => !record(drawer)
    || typeof drawer.category !== "string" || !categories.includes(drawer.category)
    || typeof drawer.text !== "string" || typeof drawer.role !== "string"
    || (drawer.textEdited !== undefined && typeof drawer.textEdited !== "boolean")
    || (drawer.replaceAsNew !== undefined && typeof drawer.replaceAsNew !== "boolean")
    || (drawer.initialText !== undefined && typeof drawer.initialText !== "string")
    || (drawer.contextId !== undefined && typeof drawer.contextId !== "string")
    || !Array.isArray(drawer.replaceIds) || drawer.replaceIds.some((id) => typeof id !== "string"))) throw invalid();
  const ids = new Set<string>();
  for (const line of lines) {
    if (!record(line) || typeof line.budgetLineId !== "string" || !line.budgetLineId || ids.has(line.budgetLineId)
      || (line.budgetOrderId !== undefined && line.budgetOrderId !== budgetOrderId)
      || typeof line.lineNo !== "number" || !Number.isInteger(line.lineNo) || line.lineNo < 0
      || typeof line.resourceType !== "string" || typeof line.unit !== "string"
      || typeof line.isLinkedToPeopleCount !== "boolean" || typeof line.isCapitaShared !== "boolean"
      || [line.quantity, line.budgetUnitPrice, line.budgetAmount].some((amount) =>
        (typeof amount !== "number" && typeof amount !== "string") || String(amount).trim() === "" || !Number.isFinite(Number(amount)))) throw invalid();
    const textFields = ["resourceEntryType", "spuTableName", "spuId", "spuNameSnapshot", "skuTableName", "skuId", "skuNameSnapshot", "itemNameSnapshot", "itemSpecSnapshot", "travelerRole", "mealType", "calcType", "peopleMode", "manualPeopleCount", "priceSourceType", "priceSourceRef", "priceBasisDesc", "remark"];
    if (textFields.some((key) => line[key] != null && typeof line[key] !== "string")
      || ["isAgentCollection", "isLocked", "recalculateLegacyBusAmount"].some((key) => line[key] !== undefined && typeof line[key] !== "boolean")
      || (line.replacesLineIds !== undefined && (!Array.isArray(line.replacesLineIds)
        || line.replacesLineIds.some(id => typeof id !== "string")))
      || ["dayCount", "nightCount"].some((key) => line[key] != null && (typeof line[key] !== "number" || !Number.isFinite(line[key])))) throw invalid();
    if (line.entrySnapshot != null) {
      const entry = line.entrySnapshot;
      if (!record(entry) || entry.schemaVersion !== 1 || entry.inputMode !== "manual_v2"
        || typeof entry.groupId !== "string" || !entry.groupId.trim() || entry.groupId.length > 100
        || typeof entry.groupLabel !== "string" || !entry.groupLabel.trim() || entry.groupLabel.length > 300
        || !["linked_people", "fixed_people", "quantity", "total"].includes(String(entry.quantityMode))
        || typeof entry.billingCount !== "number" || !Number.isSafeInteger(entry.billingCount) || entry.billingCount <= 0
        || (entry.context !== undefined && !record(entry.context))) throw invalid();
      if (record(entry.context)) {
        const context = entry.context;
        const textKeys = ["areaId", "areaName", "pointId", "pointName", "vehicleTypeId", "vehicleSpec", "fromCityCode", "fromCityName", "toCityCode", "toCityName"];
        if (textKeys.some((key) => context[key] !== undefined && typeof context[key] !== "string")
          || (context.serviceType !== undefined && !["local_charter", "pickup", "dropoff"].includes(String(context.serviceType)))
          || (context.dayNumbers !== undefined && (!Array.isArray(context.dayNumbers) || !context.dayNumbers.length
            || context.dayNumbers.some((day) => !Number.isSafeInteger(day) || day < 1)
            || new Set(context.dayNumbers).size !== context.dayNumbers.length))) throw invalid();
      }
    }
    ids.add(line.budgetLineId);
  }
  return value as QuoteBudgetLocalDraft;
}

/** 原始缓存记录可包含损坏 JSON，导出时不要求恢复成功。 */
export type QuoteBudgetRawDraftRecord = { key: string; raw: string; error?: string };

/** 枚举当前用户和预算的全部原始候选，单条损坏不会中止备份。 */
export function exportQuoteBudgetRawDrafts(userId: string, budgetOrderId: string): QuoteBudgetRawDraftRecord[] {
  return storage(() => {
    const mainKey = quoteBudgetLocalDraftKey(userId, budgetOrderId);
    const keys: string[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key === mainKey || key?.startsWith(`${mainKey}:recovery:`)) keys.push(key);
    }
    return keys.sort().flatMap((key) => {
      const raw = localStorage.getItem(key);
      if (raw === null) return [];
      try { parseQuoteBudgetLocalDraft(raw, userId, budgetOrderId); return [{ key, raw }]; }
      catch (error) { return [{ key, raw, error: error instanceof Error ? error.message : "缓存格式错误" }]; }
    });
  });
}

/** 读取当前用户和预算的缓存，保留原始值用于条件写入。 */
export function loadQuoteBudgetLocalDraft(userId: string, budgetOrderId: string): { raw: string | null; draft: QuoteBudgetLocalDraft | null } {
  const raw = storage(() => localStorage.getItem(quoteBudgetLocalDraftKey(userId, budgetOrderId)));
  return { raw, draft: raw === null ? null : parseQuoteBudgetLocalDraft(raw, userId, budgetOrderId) };
}

/** 条件写入缓存；遇到外部新值时拒绝覆盖。 */
export function writeQuoteBudgetLocalDraft(draft: QuoteBudgetLocalDraft, expectedRaw: string | null): string {
  return storage(() => {
    const key = quoteBudgetLocalDraftKey(draft.userId, draft.budgetOrderId);
    if (localStorage.getItem(key) !== expectedRaw) throw new QuoteBudgetLocalDraftError("conflict", "其他标签页已更新本机草稿，已停止覆盖；请先导出当前内容再核对。");
    const raw = JSON.stringify(draft);
    parseQuoteBudgetLocalDraft(raw, draft.userId, draft.budgetOrderId);
    localStorage.setItem(key, raw);
    return raw;
  });
}

/** 仅清理调用方最后写入的快照。 */
export function removeQuoteBudgetLocalDraft(userId: string, budgetOrderId: string, expectedRaw: string): boolean {
  return storage(() => {
    const key = quoteBudgetLocalDraftKey(userId, budgetOrderId);
    if (localStorage.getItem(key) !== expectedRaw) return false;
    localStorage.removeItem(key);
    return true;
  });
}

/** 常规写入使用浏览器跨标签独占锁，能力缺失只停用本机缓存。 */
export async function withQuoteBudgetDraftLock<T>(userId: string, budgetOrderId: string, action: () => T): Promise<T> {
  if (!navigator.locks) throw new QuoteBudgetLocalDraftError("storage", "此浏览器无法安全缓存多标签草稿；仍可编辑并保存到数据库，或导出备份。");
  try { return await navigator.locks.request(quoteBudgetLocalDraftKey(userId, budgetOrderId), action); }
  catch (error) { return storage(() => { throw error; }); }
}

/** 页面隐藏时同步保留本标签备份，不覆盖其他标签。 */
export function writeQuoteBudgetRecovery(draft: QuoteBudgetLocalDraft, writerId: string): string {
  return storage(() => {
    const raw = JSON.stringify(draft);
    parseQuoteBudgetLocalDraft(raw, draft.userId, draft.budgetOrderId);
    localStorage.setItem(`${quoteBudgetLocalDraftKey(draft.userId, draft.budgetOrderId)}:recovery:${writerId}`, raw);
    return raw;
  });
}

/** 读取卸载备份；损坏候选保留在原键并阻止自动恢复。 */
export function loadQuoteBudgetRecoveries(userId: string, budgetOrderId: string): Array<{ key: string; raw: string; draft: QuoteBudgetLocalDraft }> {
  const mainKey = quoteBudgetLocalDraftKey(userId, budgetOrderId);
  return exportQuoteBudgetRawDrafts(userId, budgetOrderId).filter((item) => item.key !== mainKey)
    .map((item) => ({ ...item, draft: parseQuoteBudgetLocalDraft(item.raw, userId, budgetOrderId) }));
}

/** 只清理已采用且未发生后续变化的卸载备份。 */
export function removeQuoteBudgetRecovery(key: string, expectedRaw: string): boolean {
  return storage(() => {
    if (localStorage.getItem(key) !== expectedRaw) return false;
    localStorage.removeItem(key);
    return true;
  });
}

/** 无锁时只写本标签独立的处理凭据，不删除或覆盖共享草稿；凭据绑定键和精确原文。 */
export function writeQuoteBudgetDraftReceipt(userId: string, budgetOrderId: string, writerId: string, records: QuoteBudgetRawDraftRecord[]): void {
  storage(() => localStorage.setItem(`${quoteBudgetLocalDraftKey(userId, budgetOrderId)}:settled:${writerId}`,
    JSON.stringify({ schemaVersion: 1, records: records.map(({ key, raw }) => ({ key, raw })) })));
}

/** 仅采纳当前身份范围的完整凭据；损坏凭据不具有忽略任何草稿的效力。 */
export function loadQuoteBudgetSettledDrafts(userId: string, budgetOrderId: string): QuoteBudgetRawDraftRecord[] {
  return storage(() => {
    const key = quoteBudgetLocalDraftKey(userId, budgetOrderId);
    const result: QuoteBudgetRawDraftRecord[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const receiptKey = localStorage.key(index);
      if (!receiptKey?.startsWith(`${key}:settled:`)) continue;
      const raw = localStorage.getItem(receiptKey);
      let receipt: unknown;
      try { receipt = JSON.parse(raw ?? "null"); } catch { continue; }
      if (!record(receipt) || receipt.schemaVersion !== 1 || !Array.isArray(receipt.records)
        || receipt.records.some(item => !record(item) || typeof item.raw !== "string" || typeof item.key !== "string"
          || (item.key !== key && !item.key.startsWith(`${key}:recovery:`)))) continue;
      result.push(...receipt.records as QuoteBudgetRawDraftRecord[]);
    }
    return result;
  });
}
